/**
 * Parse `metrics` from a technical debt item (dfp-api returns JSON as a string; some
 * pipelines may attach an object). Returns a flat object for UI display.
 */
export function parseTechnicalDebtItemMetrics(metrics: unknown): Record<string, unknown> {
  if (metrics == null || metrics === '') {
    return {};
  }
  if (typeof metrics === 'object' && !Array.isArray(metrics)) {
    return metrics as Record<string, unknown>;
  }
  if (typeof metrics === 'string') {
    const trimmed = metrics.trim();
    if (!trimmed) {
      return {};
    }
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      /* ignore invalid JSON */
    }
  }
  return {};
}

/**
 * Title-case metric key for generic "additional metrics" rows.
 */
export function humanizeTechnicalDebtMetricKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Normalize `standardized_tags` from API (string / JSON array / array) for display.
 */
export function formatTechnicalDebtStandardizedTagsDisplay(raw: unknown): string | null {
  if (raw == null || raw === '') {
    return null;
  }
  if (Array.isArray(raw)) {
    const parts = raw.map((e: unknown) => {
      if (e && typeof e === 'object' && ('category' in (e as object) || 'detail' in (e as object))) {
        const o = e as { category?: string; detail?: string };
        return [o.category, o.detail].filter(Boolean).join(' — ');
      }
      return typeof e === 'string' ? e : JSON.stringify(e);
    });
    return parts.filter(Boolean).join('; ') || null;
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return formatTechnicalDebtStandardizedTagsDisplay(parsed);
      }
    } catch {
      return raw;
    }
    return raw;
  }
  return String(raw);
}

/**
 * Normalize `tags` from API (string / JSON array / array) for display.
 */
export function formatTechnicalDebtTagsDisplay(raw: unknown): string | null {
  if (raw == null || raw === '') {
    return null;
  }
  if (Array.isArray(raw)) {
    return raw.map(String).join(', ') || null;
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map(String).join(', ') || null;
      }
    } catch {
      return raw;
    }
    return raw;
  }
  return String(raw);
}

/**
 * Single-value display for infrastructure metric cells.
 */
export function formatTechnicalDebtInfraScalarValue(key: string, val: unknown): string {
  if (val == null) {
    return '—';
  }
  if (key === 'is_multi') {
    return typeof val === 'boolean' ? (val ? 'Yes' : 'No') : String(val);
  }
  if (typeof val === 'number' && Number.isFinite(val)) {
    return String(val);
  }
  if (typeof val === 'boolean') {
    return val ? 'Yes' : 'No';
  }
  if (typeof val === 'object') {
    return JSON.stringify(val);
  }
  return String(val);
}
